import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useCreateShare, usePhotoShareState, useRevokeShares } from "@/hooks/use-shares";
import type { Photo } from "@/schemas/photos";
import type { PhotoAudienceState } from "@/schemas/shares";

function AudienceLine({
  state,
  photo,
  projectId,
}: {
  state: PhotoAudienceState;
  photo: Photo;
  projectId: string;
}) {
  const createShare = useCreateShare();
  const revokeShares = useRevokeShares();
  const busy = createShare.isPending || revokeShares.isPending;
  const change = (effect: "include" | "exclude") =>
    createShare.mutate({ projectId, audience: state.label, photoId: photo.id, effect });

  return (
    <div data-testid="share-audience" data-shared={state.shared} className="space-y-2">
      <p data-testid="share-reason" className="text-sm">
        {state.reason}
      </p>
      {state.shared ? (
        <Button
          variant="outline"
          size="sm"
          data-testid="share-exclude"
          disabled={busy}
          onClick={() => change("exclude")}
        >
          Exclude this photo
        </Button>
      ) : state.exclusionId ? (
        <Button
          variant="outline"
          size="sm"
          data-testid="share-restore"
          disabled={busy}
          onClick={() => revokeShares.mutate({ projectId, ids: [state.exclusionId ?? ""] })}
        >
          Remove exclusion
        </Button>
      ) : (
        <Button
          variant="outline"
          size="sm"
          data-testid="share-include"
          disabled={busy}
          onClick={() => change("include")}
        >
          Share this photo
        </Button>
      )}
    </div>
  );
}

/** Each firm's state of the open photo and where it comes from, with the action that changes it. */
export function PhotoShareState({ photo, projectId }: { photo: Photo; projectId: string }) {
  const { data, isPending, isError, refetch } = usePhotoShareState(photo.id);

  return (
    <section data-testid="share-state" className="space-y-3 border-t pt-4">
      <h3 className="text-xs text-muted-foreground">Sharing</h3>
      {isPending ? (
        <Skeleton className="h-10 w-full" />
      ) : isError ? (
        <Button variant="ghost" size="sm" onClick={() => refetch()}>
          Could not load the sharing state. Try again
        </Button>
      ) : data.audiences.length === 0 ? (
        <p className="text-sm text-muted-foreground">There is no IE firm to share with yet.</p>
      ) : (
        data.audiences.map((state) => (
          <AudienceLine key={state.key} state={state} photo={photo} projectId={projectId} />
        ))
      )}
    </section>
  );
}
